import React, { Component } from 'react';
import _memoize from 'lodash.memoize';
import _cloneDeep from 'lodash.clonedeep';
import _isEqual from 'lodash.isequal';
import _isEmpty from 'lodash.isempty';
import _pick from 'lodash.pick';
import cx from 'classnames';

import LinkButton from '../../Link';
import Text from '../../Text';
import MenuButton from '../../MenuButton';
import { CHECKBOX, DATE, DATE_RANGE, DROPDOWN, RADIO, MULTI_SEARCH, TAGS, TOGGLE } from '../constants';
import CheckboxFiltersGroup from './CheckboxFiltersGroup';
import RadioFiltersGroup from './RadioFiltersGroup';
import DateRangeFiltersGroup from './DateRangeFiltersGroup';
import DropdownFiltersGroup from './DropdownFiltersGroup';
import DateFiltersGroup from './DateFiltersGroup';
import MultiSearchFiltersGroup from './MultiSearchFiltersGroup';
import TagsFiltersGroup from './TagsFiltersGroup';
import { NUMERIC_MEDIA_SIZES } from '../../../common/styles/vars';

import Icon from '../../Icon';
import {
  getKeyByPath,
  getLocalStorage,
  getNumberOfActiveFilters,
  setLocalStorage } from
'../helpers';

import '../styles/_advancedFilter.scss';
import ToggleFiltersGroup from './ToggleFiltersGroup';
























const getComponent = (type) => {
  switch (type) {
    case CHECKBOX:
      return CheckboxFiltersGroup;
    case RADIO:
      return RadioFiltersGroup;
    case DATE:
      return DateFiltersGroup;
    case DATE_RANGE:
      return DateRangeFiltersGroup;
    case DROPDOWN:
      return DropdownFiltersGroup;
    case MULTI_SEARCH:
      return MultiSearchFiltersGroup;
    case TAGS:
      return TagsFiltersGroup;
    case TOGGLE:
      return ToggleFiltersGroup;
    default:
      return '';
  }
};

class AdvancedFilters extends Component {






  constructor(props) {
    super(props);

    const { name, location: { pathname } } = props;

    const selectedFilters = this.getSelectedFilters(true);
    const localStorageData = getLocalStorage(getKeyByPath(pathname, name));

    this.state = {
      selectedFilters,
      openCompactMenu: false,
      pulseBadge: false
    };

    this.filterFacetComplement = localStorageData.FILTER_COMPLEMENT;
    this.lastSelectedFilters = null; // purposely set to null to call onUpdateFilters() in the initial load.
    this.reset = false;
    // Seeded so the first pulse check has a baseline and doesn't fire on mount.
    this.badgeCount = getNumberOfActiveFilters(props.filters, selectedFilters);
  }

  componentDidMount() {
    const { initialUpdate = true } = this.props;
    const { selectedFilters } = this.state;

    if (initialUpdate && this.shouldUpdateParent()) {this.handleUpdateFilters(selectedFilters);}
  }

  componentDidUpdate(prevProps) {
    const { filters, selectedFiltersProps } = this.props;
    const { filters: prevFilters, selectedFiltersProps: prevSelectedFiltersProps } = prevProps;
    if (!_isEqual(selectedFiltersProps, prevSelectedFiltersProps)) {
      // Only absorb keys this instance owns; selectedFiltersProps also carries the
      // parent's inline filter selections, which aren't ours to manage.
      const ownedFromProps = _pick(selectedFiltersProps || {}, (filters || []).map((filter) => filter.name));

      if (!_isEmpty(ownedFromProps)) {
        this.setSelectedFilters({ ...this.state.selectedFilters, ...ownedFromProps });
      }
    }

    if (!_isEqual(filters, prevFilters)) {
      const selectedFilters = this.getSelectedFilters(true);
      const { selectedFilters: prevSelectedFilters } = this.state;

      if (!_isEqual(selectedFilters, prevSelectedFilters)) {
        this.setSelectedFilters(selectedFilters);
      }
    }

    // Pulse the count badge whenever the number of active filters actually moves.
    const badgeCount = getNumberOfActiveFilters(filters, this.state.selectedFilters);
    if (badgeCount !== this.badgeCount) {
      this.badgeCount = badgeCount;
      this.setState({ pulseBadge: true });
    }
  }

  clearPulseBadge = () => {
    if (this.state.pulseBadge) {
      this.setState({ pulseBadge: false });
    }
  };

  onFacetsGroupChange = _memoize((groupName, type) => (facets) => {
    const updatedFacets = type === DROPDOWN ?
    facets.map((item) => item.value) :
    facets;
    const updatedFilter = { [groupName]: updatedFacets };
    const { name, location: { pathname } } = this.props;

    const { selectedFilters } = this.state;
    const updatedFilters = { ...selectedFilters, ...updatedFilter };

    const updateFilterFacetComplement = { ...this.filterFacetComplement };
    if (type === DROPDOWN) {
      // only add if not from filterFacet
      updateFilterFacetComplement[groupName] = this.getFilterFacetComplement(facets, groupName);

      this.filterFacetComplement = updateFilterFacetComplement;
    }
    setLocalStorage(getKeyByPath(pathname, name), {
      SELECTED_VALUES: updatedFilters,
      FILTER_COMPLEMENT: updateFilterFacetComplement
    });

    this.setState({
      selectedFilters: updatedFilters
    }, () => {
      if (this.shouldUpdateParent()) {
        this.handleUpdateFilters(updatedFilters);
      }
    });
  });

  setSelectedFilters = (selectedFilters) => {
    this.setState({ selectedFilters }, () => {
      if (this.shouldUpdateParent()) {
        this.handleUpdateFilters(selectedFilters);
      }
    });
  };

  getFilterFacetComplement = (facets, name) => {
    const { filters } = this.props;
    const { filterFacets } = (filters || []).find((e) => e.name === name) || {};
    // $FlowFixMe
    return facets.filter((e) => !(filterFacets || []).
    find((ff) => ff.label === e.label && ff.value === e.value)
    );
  };

  resetFilters = () => {
    const selectedFilters = this.getSelectedFilters(false, true);
    // Update state and call parent update function
    this.setState({ selectedFilters }, () => {
      const { name, location: { pathname } } = this.props;

      if (this.shouldUpdateParent()) {
        this.handleUpdateFilters(selectedFilters);
      }
      // if persistent, store in local storage
      setLocalStorage(getKeyByPath(pathname, name), {
        SELECTED_VALUES: selectedFilters,
        FILTER_COMPLEMENT: {}
      });
    });
  };

  getSelectedFilters = (isInit, isReset) => {
    const { name,
      filters,
      shouldReturnOptionAllSelections,
      location: { pathname },
      isPersistent
    } = this.props;

    if (!filters) {
      return {};
    }

    let selectedFilters = {};

    filters.forEach((filter) => {
      const { name: filterName, filterFacets } = filter;

      if (typeof selectedFilters[filterName] !== 'undefined') {
        return;
      }

      const defaultFacets = filterFacets ? filterFacets.filter((facet) => facet.selectedDefault) : [];
      const defaultValues = defaultFacets.length ? defaultFacets.reduce((acc, defaultFacet) => {
        if (defaultFacet && (isInit || isReset) && (shouldReturnOptionAllSelections || String(defaultFacet.value).toLowerCase() !== 'all')) {
          let defaultValue = defaultFacet.value;
          if (String(defaultFacet.value).includes(',')) {
            defaultValue = defaultFacet.value.split(',');
          }
          if (String(defaultFacet.value) === 'null') {
            defaultValue = null;
          }

          const values = Array.isArray(defaultValue) ? defaultValue : [defaultValue];
          return acc.concat(values);
        }
        return acc;
      }, []) : [];
      // Store the accumulated values in selectedFilters
      if (defaultValues.length) {
        selectedFilters[filterName] = defaultValues;
      }
    });

    // if persistent and initialising override with localStorage
    if (isPersistent && isInit) {
      const { SELECTED_VALUES } = getLocalStorage(getKeyByPath(pathname, name));
      // The store is keyed by pathname, so every config rendered on this path shares it —
      // Traffic's five queue tabs being the live case. Only hydrate keys the CURRENT config
      // owns: a key persisted under another config (EDITING_TEAM on the Global tab) must not
      // leak into a config with no facet for it (the Team tab), because the parent trusts
      // this echo over its own queue scoping (#3763).
      selectedFilters = {
        ...selectedFilters,
        ..._pick(SELECTED_VALUES || {}, filters.map((filter) => filter.name))
      };
    }

    return selectedFilters;
  };

  shouldUpdateParent = () => {
    const { filters, onUpdateFilters } = this.props;

    return filters && onUpdateFilters;
  };

  toggleCompactMenu = ({ closeMenu = false }) => {
    this.setState(({ openCompactMenu }) => ({
      openCompactMenu: closeMenu ? false : !openCompactMenu
    }));
  };

  handleUpdateFilters(selectedFilters) {
    const { onUpdateFilters } = this.props;

    if (!_isEqual(selectedFilters, this.lastSelectedFilters)) {
      this.lastSelectedFilters = _cloneDeep(selectedFilters);
      if (onUpdateFilters) onUpdateFilters(selectedFilters);
    }
  }

  renderFiltersGroup = (filter, stats) => {
    const {
      type,
      name,
      label,
      openedByDefault,
      filterFacets,
      additionalOptions,
      minWidth
    } = filter;
    const { isCollapsible, shouldReturnOptionAllSelections } = this.props;
    const { selectedFilters } = this.state;

    const ComponentName = getComponent(type);

    return (
      ComponentName ?
      <ComponentName
        key={name}
        name={name}
        filterFacets={
        this.filterFacetComplement[name] ?
        [...(filterFacets || []), ...this.filterFacetComplement[name]] :
        filterFacets}

        label={label}
        isCollapsible={isCollapsible}
        openedByDefault={openedByDefault}
        onChange={this.onFacetsGroupChange(name, type)}
        selectedValues={selectedFilters[name]}
        minWidth={minWidth}
        stats={stats}
        shouldReturnOptionAllSelection={shouldReturnOptionAllSelections}
        {...additionalOptions} /> :

      null);

  };

  render() {
    const {
      filters,
      containerSize,
      numResults,
      stats,
      isCompactMenuButton
    } = this.props;
    const { selectedFilters, openCompactMenu, pulseBadge } = this.state;

    if (!filters) {return null;}

    const numActiveFilters = getNumberOfActiveFilters(filters, selectedFilters);

    const buttonLabel =
    <>
        <Text>Filters</Text>
        {
      numActiveFilters ?
      <div
        className={cx('advanced-filters__badge', {
          'advanced-filters__badge--pulse': pulseBadge
        })}
        onAnimationEnd={this.clearPulseBadge}>{numActiveFilters}</div> :
      null}

      </>;


    const isSmall = containerSize ? containerSize.width : NUMERIC_MEDIA_SIZES.small > 0;

    const menuButtonPrefix = isCompactMenuButton ?
    <div className="advanced-filters__compact-menu-icon">
        <Icon name="preferences" size={15} />
        <div
        className={cx('advanced-filters__badge-v2', {
          'advanced-filters__badge-v2--pulse': pulseBadge
        })}
        onAnimationEnd={this.clearPulseBadge}>{numActiveFilters}</div>
      </div> :
    <Icon name="preferences" size={15} />;

    return (
      <div
        className={cx('advanced-filters', {
          'advanced-filters--compact-menu-button': isCompactMenuButton,
          'advanced-filters--active-compact-menu': isCompactMenuButton && openCompactMenu
        })}>

        <MenuButton
          fallbackPlacements={['bottom']}
          buttonProps={{
            children: buttonLabel,
            prefix: menuButtonPrefix
          }}
          align="end"
          id="advancedFiltersButton"
          isCompact={isCompactMenuButton}
          toggleCompactMenu={this.toggleCompactMenu}>

          <div className="advanced-filters__header">
            {numResults && stats && containerSize ?
            <div
              className={cx('inner-advanced--filters', {
                'inner-advanced-filters--medium': isSmall
              })}>

                <div className="inner-advanced-filters__header">
                  <Text>Showing</Text>
                  <Text>
                    {numResults || '   '}
                    {' '}
                    of
                    {' '}
                    {stats ? stats.global.total : '   '}
                    {' '}
                    videos
                  </Text>
                </div>
              </div> :
            null}

            <LinkButton
              className="advanced-filters__header__reset"
              onClick={this.resetFilters}>

              Reset Filters
            </LinkButton>
          </div>
          {filters.map((filter) =>
          this.renderFiltersGroup(filter, stats)
          )}
        </MenuButton>
      </div>);

  }
}

export default AdvancedFilters;